/**
 * 6DOF rigid body + semi-implicit (symplectic) Euler integrator.
 *
 * Semi-implicit Euler updates velocity first, then position from the NEW
 * velocity — far more stable than explicit Euler for the fixed 120 Hz sim step.
 *
 * Flight-assist applies exponential linear/angular damping each step (toggleable).
 * With assist OFF the craft is pure Newtonian: it coasts forever (Elite "FA-off").
 */
import { add, scale, vec3, type Vec3 } from './vec3.js';
import { identity, integrateOrientation, type Quat } from './quat.js';

export interface InertiaTensor {
  /** Diagonal principal moments of inertia (kg*m^2), body axes. */
  ix: number;
  iy: number;
  iz: number;
}

export interface RigidBody {
  position: Vec3; // world frame, Float64
  velocity: Vec3; // world frame, m/s (sim units)
  orientation: Quat;
  angularVelocity: Vec3; // body frame, rad/s
  mass: number; // kg
  inertia: InertiaTensor;
  /** Flight-assist linear damping coefficient (per second). 0 = no damping. */
  linearDamping: number;
  /** Flight-assist angular damping coefficient (per second). */
  angularDamping: number;
}

export const makeRigidBody = (overrides: Partial<RigidBody> = {}): RigidBody => ({
  position: vec3(),
  velocity: vec3(),
  orientation: identity(),
  angularVelocity: vec3(),
  mass: 1000,
  inertia: { ix: 1, iy: 1, iz: 1 },
  linearDamping: 0,
  angularDamping: 0,
  ...overrides,
});

/** Exponential damping factor for coefficient k over dt: v *= e^{-k*dt}. */
const dampFactor = (k: number, dt: number): number => (k > 0 ? Math.exp(-k * dt) : 1);

/**
 * Advance the body one fixed step.
 * @param force  net world-frame force (N), e.g. gravity + thrust
 * @param torque net body-frame torque (N*m)
 * @param assist when true, apply linear/angular damping (flight-assist on)
 */
export const integrate = (
  body: RigidBody,
  force: Vec3,
  torque: Vec3,
  dt: number,
  assist: boolean,
): RigidBody => {
  const invMass = 1 / body.mass;

  // Linear: a = F/m, semi-implicit (update v, then x from new v).
  let velocity = add(body.velocity, scale(force, invMass * dt));
  if (assist) velocity = scale(velocity, dampFactor(body.linearDamping, dt));
  const position = add(body.position, scale(velocity, dt));

  // Angular half is shared with integrateAngular (single source of truth).
  const rotated = integrateAngular(body, torque, dt, assist);

  return { ...body, position, velocity, orientation: rotated.orientation, angularVelocity: rotated.angularVelocity };
};

/**
 * M4 CONTROL-FEEL: the ANGULAR half of `integrate`, standalone — position and
 * velocity pass through untouched. Lets supercruise marry its kinematic
 * translation (LOCKED speed model) to real servo rotation.
 */
export const integrateAngular = (
  body: RigidBody,
  torque: Vec3,
  dt: number,
  assist: boolean,
): RigidBody => {
  // alpha = I^-1 * tau (diagonal inertia, body frame), semi-implicit.
  const angAccel: Vec3 = {
    x: torque.x / body.inertia.ix,
    y: torque.y / body.inertia.iy,
    z: torque.z / body.inertia.iz,
  };
  let angularVelocity = add(body.angularVelocity, scale(angAccel, dt));
  if (assist) angularVelocity = scale(angularVelocity, dampFactor(body.angularDamping, dt));

  const orientation = integrateOrientation(body.orientation, angularVelocity, dt);

  return { ...body, orientation, angularVelocity };
};

/**
 * FLIGHT-FEEL A1: rotational drive parameters. A craft no longer snaps its
 * angular velocity to the commanded rate — instead a servo applies TORQUE that
 * pushes angVel toward the target rate, bounded by maxTorque. Heavier inertia
 * therefore takes longer to spin up/down, which is the "weight" the player feels.
 */
export interface AngularDrive {
  /** Per-axis maximum angular rate (rad/s) reachable at full command. */
  maxRate: Vec3;
  /** Per-axis servo gain (1/s) — how aggressively torque chases the rate error. */
  accel: Vec3;
  /** Per-axis torque clamp (N*m) so the servo can't apply infinite torque. */
  maxTorque: Vec3;
}

const clampAbs = (v: number, lim: number): number => Math.max(-lim, Math.min(lim, v));

/**
 * FLIGHT-FEEL A1: per-axis command torque for a rate-servo controller.
 * For each axis: tau = clamp( I * accel * (cmd*maxRate - angVel), ±maxTorque ).
 * `cmd` components are -1..1. With I in the gain the resulting angular accel is
 * `accel * (target - angVel)` (a first-order lag toward the target rate), which
 * is mass-independent in TIME-CONSTANT but mass-dependent in TORQUE — exactly the
 * felt weight. STABILITY: choose accel so `accel*dt < ~2` at the 120 Hz step
 * (the M3 tuning gives accel≈11 → 11/120 ≈ 0.09, well within bound).
 */
export const commandTorque = (body: RigidBody, cmd: Vec3, drive: AngularDrive): Vec3 => {
  const { ix, iy, iz } = body.inertia;
  const w = body.angularVelocity;
  return {
    x: clampAbs(ix * drive.accel.x * (cmd.x * drive.maxRate.x - w.x), drive.maxTorque.x),
    y: clampAbs(iy * drive.accel.y * (cmd.y * drive.maxRate.y - w.y), drive.maxTorque.y),
    z: clampAbs(iz * drive.accel.z * (cmd.z * drive.maxRate.z - w.z), drive.maxTorque.z),
  };
};

/** Kinetic energy (translational + rotational) — used by energy-sanity tests. */
export const kineticEnergy = (body: RigidBody): number => {
  const v2 = body.velocity.x ** 2 + body.velocity.y ** 2 + body.velocity.z ** 2;
  const w = body.angularVelocity;
  const rot = body.inertia.ix * w.x ** 2 + body.inertia.iy * w.y ** 2 + body.inertia.iz * w.z ** 2;
  return 0.5 * body.mass * v2 + 0.5 * rot;
};
